/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    shape, string, arrayOf,
} from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import CloseIcon from '@material-ui/icons/Close';
import { useUIDSeed } from 'react-uid';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ReactHtmlParser from 'react-html-parser';
import { Link, useLocation } from 'react-router-dom';
import OfferTimer from './OfferTimer';
import CTA from '../../ContentTypeComponents/Buttons/CTA';
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getPageType } from '../../../../state/ducks/App/App-Selectors';

// strips "font-family:" and ";" from value
const parseFont = (font) => font?.match(/:\s(.*?);/, '')?.[1] || null;

const useStyles = makeStyles((theme) => ({
    banner: (styling) => ({
        backgroundColor: styling.styling?.colors?.banner_background?.color || '#000',
        fontFamily: parseFont(styling.styling?.fonts?.main) || 'inherit',
    }),
    bannerImage: (styling) => ({
        display: 'block',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'center',
        padding: '6px 0',
        '& span': {
            marginLeft: '10px',
            cursor: 'pointer',
            color: styling.styling.colors?.subheading?.color || theme.palette.yellow || '#ffffbf',
        },
        [theme.breakpoints.down(1030)]: {
            fontSize: '18px',
        },
    }),
    bannerImageContainer: (styling) => ({
        backgroundColor: styling.styling?.colors?.banner_background?.color || theme.palette.cms?.black || '#000',
        cursor: 'pointer',
        color: styling.styling.colors?.heading?.color || theme.palette.cms?.white || '#ffffff',
        display: 'flex',
        alignItems: 'center',
        padding: '8px 0',
    }),

    promotionDetailContainer: {
        backgroundColor: theme.palette.lightGray || '#F1F0EC',
        fontFamily: 'Helvetica, Arial, sans-serif',
    },
    promoContent: {
        fontSize: '16pt',
        fontWeight: 'bold',
        textAlign: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        maxWidth: '920px',
        width: '100%',
        margin: '0 auto',

    },
    promoCodeContentPrice: {
        width: '50%',
        display: 'flex',
        alignItems: 'center',
        padding: '0',
        paddingBottom: '8px',
        cursor: 'pointer',
        '&:nth-child(even)': {
            paddingLeft: '4%',
        },
        '&:nth-child(odd)': {
            borderRight: `1px solid ${theme.palette.divider || '#ccc'}`,
        },
    },
    promoPrice: {
        verticalAlign: 'middle',
        color: theme.palette.red || '#a1191f',
    },

    offerImage: {
        display: 'flex',
        cursor: 'pointer',
        marginRight: '12px',
        '& img': {
            width: '70px',
        },
    },
    promoText: (styling) => ({
        color: theme.palette.red || '#ae2016',
        maxWidth: '43%',
        margin: '0 auto',
        fontSize: '20px',
        fontWeight: 'bold',
        lineHeight: '24px',
        padding: '20px 0px',
        textAlign: 'center',
        '& a': {
            cursor: 'pointer',
            color: styling.styling.colors?.offer_heading?.color || theme.palette.darkRed || '#900',
        },
        '& p': {
            margin: '0 auto',
        },
    }),
    promoCodeContent: {
        color: theme.palette.gray || '#666666',
        fontSize: '10px',
        fontWeight: ' normal',
        width: '62%',
        margin: '0 auto',
        textAlign: 'center',
        [theme.breakpoints.down(600)]: {
            width: '95%',
        },
    },
    closeButton: (styling) => ({
        font: 'bold 11px verdana',
        color: styling.styling.colors?.offer_heading?.color || theme.palette.red || '#a1191f',
        backgroundColor: 'transparent',
        border: 'none',
        width: '100%',
        cursor: 'pointer',
        padding: '25px',
        margin: '0 auto',
        textAlign: 'center',
        '&:focus': {
            outline: 'none',
        },
    }),
    categoryOffPrice: (styling) => ({
        color: styling.styling.colors?.offer_heading?.color || theme.palette.red || '#ae2016',
        textDecoration: 'none',
    }),
    categoryTitleName: {
        color: theme.palette.black || '#000',
        paddingLeft: '5px',
        textDecoration: 'none',
    },
    downArrowIcon: (styling) => ({
        color: styling.styling.colors?.subheading?.color || theme.palette.white || '#ffffff',
        width: '2em',
        verticalAlign: 'middle',
    }),
    categoryContainer: {
        display: 'flex',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    categoryDescription: (styling) => ({
        textDecoration: 'none',
        color: styling.styling.colors?.offer_subheading?.color || theme.palette.black || '#000',
        [theme.breakpoints.down(600)]: {
            fontSize: '19px',
        },
    }),
    innerContainer: () => ({
        display: 'flex',
        justifyContent: 'space-around',
        width: '100%',
        margin: '0 auto',
        maxWidth: '1200px',

        [theme.breakpoints.down(600)]: {
            flexDirection: 'column',
            alignItems: 'center',
        },
    }),
    copyAndCountdown: (styling) => ({
        display: 'flex',
        flexDirection: styling.styling.position || 'row',
        flex: 1,
        justifyContent: 'space-evenly',
        [theme.breakpoints.down(600)]: {
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            '&>div': {
                padding: '5px 0',
                display: 'flex',
                flexDirection: 'column',
                alignContent: 'space-between',
            },
        },
    }),
    hideCountdown: {
        width: '100%',
    },
    arrowDrop: (styling) => ({
        color: styling.styling.colors?.subheading?.color || '',
        fontSize: '0px',
    }),
    flipArrow: {
        /*
            TODO: Needs to rotate upside-down.
            Current SVG has dimensions that prohibitively offsets axis of rotation
        */
        opacity: 0,
    },
    hideArrow: {
        display: 'none',
    },
    hidden: {
        opacity: 0,
        [theme.breakpoints.down(600)]: {
            display: 'none',
        },
    },
    ctaContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 15px 0 0',
        [theme.breakpoints.down(600)]: {
            padding: '0',
            '&>a[data-testid="cta-anchor"]>button[type="button"]': {
                height: '26px',
                fontSize: '.9em',
            },
        },
    },
    hideBanner: {
        display: 'none',
        [theme.breakpoints.down(600)]: {
            display: 'block',
            position: 'absolute',
            top: '0',
            right: '0',
        },
    },
}));

const OfferBanner = (props) => {
    const {
        menu, url,
    } = props;
    const cta = menu.cta?.linking?.link?.href?.length ? menu.cta : '';
    const countDownHide = menu?.hide_countdown || false;
    const dropDownHide = menu?.dropdown_hide || false;
    const offerBannerHeader = menu?.banner_header || {};
    const title = menu?.offer_block?.title  || '';
    const offerImage = menu?.offer_block?.offers  || [];
    const [open, setopen] = useState(false);
    const [hide, setHide] = useState(false);
    const location = useLocation();
    const seed = useUIDSeed();
    const countDownDate = new Date(menu?.countdown_date).getTime();
    const nowTime = new Date().getTime();
    const toggleButton = () => {
        setopen(!open);
    };
    const hideBanner = () => {
        setHide(!hide);
    };
    const styles = menu.styling;
    const styling = { ...styles };
    styling.position = menu.countdown_position;
    const classes = useStyles({ styling });
    const pageType = useSelector(getPageType) || 'default';

    const urlIsNotExcluded = (bySpecificPath, currentURL) => {
        const pathList = bySpecificPath.filter((item) => item.indexOf('*') >= 0);
        let urlFound = false;
        pathList.forEach((path) => {
            const specificPath = path.split('*')?.[0];
            if (currentURL.indexOf(specificPath) >= 0) {
                urlFound = true;
            }
        });
        return urlFound;
    };

    // showing the banner in specific page and URL
    const noPageExclusions = (exclusions) => {
        let currentUrl = '';
        if (typeof window !== 'undefined') currentUrl = location.pathname || window?.location?.pathname || url;
        if (pageType === 'notfound' || exclusions?.byPageType?.includes(pageType) || exclusions?.bySpecificPath?.includes(currentUrl) || urlIsNotExcluded(exclusions.bySpecificPath, currentUrl)) return false;
        return true;
    };
    const exclusions = {
        byPageType: menu?.bulk_exceptions || [],
        bySpecificPath: menu?.granular_exceptions || [],
    };

    const ctaButton = () => (
        <CTA
            href={cta.linking.link.href}
            copy={cta.linking.link.title || 'Click Here'}
            fontFamily={styling.fonts.cta_font}
            fontColor={styling.colors.cta_font_color?.color}
            backgroundColor={styling.colors.cta_background_color?.color}
            tracking_event_category={cta.linking?.tracking_event_category || cta.linking.link?.tracking_event_category}
            tracking_event_action={cta.linking?.tracking_event_action || cta.linking.link?.tracking_event_action}
            tracking_event_label={cta.linking?.tracking_event_label || cta.linking.link?.tracking_event_label}
            addRadius={cta.border_radius}
        />
    );
    const countdown = () => <OfferTimer dateCountDown={menu.countdown_date} styling={styling} />;

    const renderCountdown = !countDownHide && nowTime < countDownDate;

    return (
        noPageExclusions(exclusions) && (
            <div className={classes.banner}>
                {!hide
                && (
                    <div
                        className={classes.bannerImageContainer}
                        role="presentation"
                        onClick={() => toggleButton()}
                    >
                        <div className={classes.innerContainer}>
                            <div className={classes.copyAndCountdown}>
                                <div className={`${classes.bannerImage} ${countDownHide ? classes.hideCountdown : ''}`}>{offerBannerHeader?.heading}
                                    <span>{offerBannerHeader?.subheading}</span>
                                    <ArrowDropDownIcon className={`${countDownHide ? classes.arrowDrop : classes.downArrowIcon} ${open && classes.flipArrow} ${dropDownHide && classes.hideArrow }`}  />
                                </div>
                                { renderCountdown && countdown()}
                            </div>

                            { cta && <div className={classes.ctaContainer}>{ctaButton()}</div> }
                        </div>
                        <div className={classes.hideBanner} onClick={() => hideBanner()} role="none">
                            <CloseIcon />
                        </div>
                    </div>
                )                }
                {open && !dropDownHide
                && (
                    <div className={classes.promotionDetailContainer}>
                        <div className={classes.promoText}>
                            <p>{ReactHtmlParser(title)}</p>
                        </div>
                        <div className={classes.promoContent}>
                            {
                                offerImage.map((item) => (
                                    <div className={classes.promoCodeContentPrice} key={seed(item)}>
                                        <div className={classes.offerImage}>
                                            <img src={item?.image?.url} alt="Product_Img" />
                                        </div>
                                        <div className={classes.categoryContainer}>
                                            <Link
                                                className={classes.categoryDescription}
                                                to={item?.link?.href}
                                            >
                                                <span className={classes.categoryOffPrice}>
                                                    {item?.category_off}
                                                </span>
                                                {` ${item?.link?.title}`}
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                        <div className={classes.promoCodeContent}>
                            <p>{menu?.copy}</p>
                        </div>
                        <div className={classes.closeButton}>
                            <span role="presentation" onClick={() => toggleButton()}>
                                Close
                            </span>
                        </div>
                    </div>
                )}
            </div>
        )
    );
};

OfferBanner.defaultProps = {
    url: '/',
};

OfferBanner.propTypes = {
    menu: shape({
        banner_header: shape({
            heading: string,
            subheading: string,
        }).isRequired,
        copy: string,
        offer_block: shape({
            offers: arrayOf(
                shape({
                    image: shape({
                        url: string,
                    }).isRequired,
                    link: shape({
                        title: string,
                    }).isRequired,
                    category_off: string,
                }).isRequired,
            ).isRequired,
            title: string,
        }).isRequired,
    }).isRequired,
    url: string,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

export default connect(
    null,
    mapDispatchToProps,
)(OfferBanner);
